import { Component } from 'react'
import styled from 'styled-components'
import { X, User, Building, Banknote, TrendingDown, Calendar, Info } from 'lucide-react'
import { formatCurrency } from '../Tools/CurrencyConverter'

export default class CalendarEventDetail extends Component {
  handleClose = (e) => {
    if (e.target === e.currentTarget || e.type === 'click') {
      this.props.onClose()
    }
  }

  formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  formatIBAN = (iban) => {
    if (!iban || iban === '-') return '-'
    // Formatta IBAN con spazi ogni 4 caratteri per leggibilità
    return iban.replace(/(.{4})/g, '$1 ').trim()
  }

  render () {
    const { event } = this.props

    if (!event) return null

    const eventType = event.eventType || 'manual'
    const cliente = event.cliente
    const hasAmount = event.amount !== null && event.amount !== undefined

    const eventColor = eventType === 'cedola' ? '#10B981' : eventType === 'costo' ? '#EF4444' : '#3B82F6'
    const eventLabel = eventType === 'cedola' ? 'Cedola' : eventType === 'costo' ? 'Costo' : 'Evento'

    const EventIconComponent = () => {
      if (eventType === 'cedola') return <Banknote size={28} />
      if (eventType === 'costo') return <TrendingDown size={28} />
      return <Calendar size={28} />
    }

    return (
      <ModalOverlay onClick={this.handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader $color={eventColor}>
            <HeaderContent>
              <EventIconBox $color={eventColor}>
                <EventIconComponent />
              </EventIconBox>
              <HeaderText>
                <EventType>{eventLabel}</EventType>
                <EventTitle>{event.title}</EventTitle>
                <EventDate>{this.formatDate(event.startDate)}</EventDate>
              </HeaderText>
            </HeaderContent>
            <CloseButton onClick={this.handleClose} title='Chiudi'>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {hasAmount && (
              <Section>
                <AmountDisplay $color={eventColor}>
                  <AmountLabel>Importo</AmountLabel>
                  <AmountValue>{formatCurrency(event.amount, event.currency)}</AmountValue>
                </AmountDisplay>
              </Section>
            )}

            {event.description && (
              <Section>
                <SectionTitle>Descrizione</SectionTitle>
                <Description>{event.description}</Description>
              </Section>
            )}

            {cliente && (
              <Section>
                <SectionTitle>
                  <User size={16} />
                  <span>Informazioni Cliente</span>
                </SectionTitle>

                <ClientCard>
                  <ClientName>
                    {cliente.nome && cliente.cognome
                      ? `${cliente.nome} ${cliente.cognome}`
                      : cliente.intestazione || 'Cliente'}
                  </ClientName>

                  <BankingInfo>
                    <SectionSubtitle>
                      <Building size={14} />
                      <span>Dati Bancari</span>
                    </SectionSubtitle>

                    <InfoGrid>
                      <InfoRow>
                        <InfoLabel>Banca:</InfoLabel>
                        <InfoValue>{cliente.banca || '-'}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>IBAN:</InfoLabel>
                        <InfoValue $mono>{this.formatIBAN(cliente.iban)}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>SWIFT/BIC:</InfoLabel>
                        <InfoValue>{cliente.swift || '-'}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>Beneficiario:</InfoLabel>
                        <InfoValue>{cliente.nomeBeneficiario || '-'}</InfoValue>
                      </InfoRow>
                    </InfoGrid>
                  </BankingInfo>
                </ClientCard>
              </Section>
            )}

            {!cliente && eventType !== 'manual' && (
              <EmptyState>
                <EmptyIconBox>
                  <Info size={32} />
                </EmptyIconBox>
                <EmptyText>Nessun cliente associato a questo {eventLabel.toLowerCase()}</EmptyText>
              </EmptyState>
            )}
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    )
  }
}

// ============ STYLED COMPONENTS ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'};
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.card};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
`

const ModalHeader = styled.div`
  position: relative;
  background: ${props => props.$color}15;
  border-bottom: 3px solid ${props => props.$color};
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`

const EventIconBox = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  flex-shrink: 0;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const EventType = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.text.tertiary};
`

const EventTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  line-height: 1.3;
`

const EventDate = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  text-transform: capitalize;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${props => props.theme.radius.button};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
  }

  &:active {
    transform: scale(0.95);
  }
`

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 1.5rem;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.secondary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.border.strong};
  }
`

const AmountDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: ${props => props.$color}15;
  border: 2px solid ${props => props.$color}40;
  border-radius: ${props => props.theme.radius.input};
  gap: 0.5rem;
`

const AmountLabel = styled.div`
  font-size: 0.813rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const AmountValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.5px;
`

const Section = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  svg {
    color: ${props => props.theme.colors.text.secondary};
  }
`

const SectionSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.813rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.75rem;

  svg {
    color: ${props => props.theme.colors.text.tertiary};
  }
`

const Description = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`

const ClientCard = styled.div`
  background: ${props => props.theme.colors.background.hover};
  border-radius: ${props => props.theme.radius.input};
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
`

const ClientName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
`

const BankingInfo = styled.div`
  /* Contenitore per dati bancari */
`

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 100px 1fr;
    gap: 0.5rem;
  }
`

const InfoLabel = styled.div`
  font-size: 0.813rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.tertiary};
`

const InfoValue = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
  word-break: break-all;
  font-family: ${props => props.$mono ? 'monospace' : 'inherit'};
  letter-spacing: ${props => props.$mono ? '0.5px' : 'normal'};
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 0.75rem;
`

const EmptyIconBox = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.tertiary};
  opacity: 0.7;
`

const EmptyText = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.muted};
  text-align: center;
  margin: 0;
`
